import React, { useMemo } from "react";

import { Alert, Stack, Typography } from "@mui/material";
import { currencySign } from "constants/index";
import { CurrencyType } from "store/types";

import { LocalCharacteristicData, useDealCreateContext } from "../context/DealCreateContext";
import { NavigationButtons } from "./NavigationButtons";

interface SummaryRow {
  label: string;
  value: string;
  error?: boolean;
}

interface SummarySection {
  title: string;
  rows: SummaryRow[];
}

export const SummaryScreen: React.FC = () => {
  const { state, goBack, handleSubmit, submitting, characteristics, stages, setField } = useDealCreateContext();

  // Required characteristics for selected stage
  const requiredCharacteristics = useMemo(() => {
    const selectedStage = stages.find((s) => s.id === state.stageId);
    if (!selectedStage) return [];
    const statusId = selectedStage.status_id;
    return characteristics.filter((c) => c.required_to_status_ids?.includes(statusId));
  }, [stages, state.stageId, characteristics]);

  const missingRequiredCharIds = useMemo(() => {
    return new Set(
      requiredCharacteristics
        .filter((rc) => !state.characteristicsData.some((cd) => cd.characteristic_id === rc.id && cd.value))
        .map((rc) => rc.id)
    );
  }, [requiredCharacteristics, state.characteristicsData]);

  const basicFieldsMissing = !state.dealName.trim() || !state.assetClass || !state.stageId || !state.currency;
  const requiredCharsMissing = missingRequiredCharIds.size > 0;
  const isCreateDisabled = submitting || basicFieldsMissing || requiredCharsMissing;

  const sections = useMemo<SummarySection[]>(() => {
    const result: SummarySection[] = [];

    // Basic Fields
    const basicRows: SummaryRow[] = [];
    basicRows.push({
      label: "Deal Name",
      value: state.dealName || "",
      error: !state.dealName.trim()
    });
    basicRows.push({
      label: "Asset Class",
      value: state.assetClass ? state.assetClass.account_alias || state.assetClass.name : "",
      error: !state.assetClass
    });
    basicRows.push({
      label: "Currency",
      value: state.currency || "",
      error: !state.currency
    });
    if (state.geography) basicRows.push({ label: "Geography", value: state.geography });
    result.push({ title: "BASIC FIELDS", rows: basicRows });

    // Group characteristics data by category
    const categoryMap = new Map<string, SummaryRow[]>();

    // Group entries by characteristic_id to handle multiple entries
    const entriesByCharId = new Map<number, LocalCharacteristicData[]>();
    state.characteristicsData.forEach((cd) => {
      if (!cd.value) return;
      const existing = entriesByCharId.get(cd.characteristic_id) || [];
      existing.push(cd);
      entriesByCharId.set(cd.characteristic_id, existing);
    });

    entriesByCharId.forEach((entries, charId) => {
      const charDef = characteristics.find((c) => c.id === charId);
      if (!charDef) return;

      const category = charDef.category_name || "General";

      // Skip Currency and Geography — already in Basic Fields
      if (charDef.name === "Currency" || charDef.name === "Geography") return;

      const rows = categoryMap.get(category) || [];

      const isCurrencyType = charDef.data_type === "currency";

      if (entries.length === 1) {
        const sign = isCurrencyType && entries[0].currency
          ? currencySign[entries[0].currency as CurrencyType] || entries[0].currency
          : "";
        const displayValue = sign ? `${sign}${entries[0].value}` : entries[0].value;
        rows.push({ label: charDef.name, value: displayValue });
      } else {
        const formatted = entries
          .map((e) => {
            const sign = isCurrencyType && e.currency
              ? currencySign[e.currency as CurrencyType] || e.currency
              : "";
            const val = sign ? `${sign}${e.value}` : e.value;
            return `${val} (${e.percentage}%)`;
          })
          .join(", ");
        rows.push({ label: charDef.name, value: formatted });
      }

      categoryMap.set(category, rows);
    });

    // Add missing required characteristics as error rows
    for (const rc of requiredCharacteristics) {
      if (missingRequiredCharIds.has(rc.id)) {
        const category = rc.category_name || "General";
        const rows = categoryMap.get(category) || [];
        rows.push({ label: rc.name, value: "", error: true });
        categoryMap.set(category, rows);
      }
    }

    // Prepend Sub Asset Class to General category
    if (state.subAssetClass) {
      const generalRows = categoryMap.get("General") || [];
      generalRows.unshift({
        label: "Sub Asset Class",
        value: state.subAssetClass.account_alias || state.subAssetClass.name
      });
      categoryMap.set("General", generalRows);
    }

    categoryMap.forEach((rows, category) => {
      result.push({ title: category.toUpperCase(), rows });
    });

    // Contact
    const contactRows: SummaryRow[] = [];
    const cs = state.contact;
    if (cs.first_name || cs.last_name) {
      contactRows.push({ label: "Name", value: `${cs.first_name} ${cs.last_name}`.trim() });
    }
    if (cs.email) contactRows.push({ label: "Email", value: cs.email });
    if (cs.firm) contactRows.push({ label: "Firm", value: cs.firm });
    if (cs.title) contactRows.push({ label: "Title", value: cs.title });
    if (cs.phone_number) contactRows.push({ label: "Phone", value: cs.phone_number });
    if (contactRows.length) result.push({ title: "CONTACT", rows: contactRows });

    return result;
  }, [state, characteristics, requiredCharacteristics, missingRequiredCharIds]);

  return (
    <>
      <Stack sx={{ padding: "32px", gap: "20px", flexGrow: 1, overflowY: "auto" }}>
        {state.error && (
          <Alert severity="error" onClose={() => setField("error", null)}>
            {state.error}
          </Alert>
        )}

        <Stack sx={{ gap: "3px" }}>
          <Typography sx={{ fontSize: "15px", fontWeight: 500, lineHeight: "20px", color: "#071F35" }}>
            Deal Summary
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 400, lineHeight: "16px", color: "#496682" }}>
            Review the deal details before creating it.
          </Typography>
        </Stack>

        {sections.map((section) => (
          <Stack key={section.title} sx={{ gap: "3px" }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 300,
                lineHeight: "16px",
                color: "#071F35",
                textTransform: "uppercase"
              }}
            >
              {section.title}
            </Typography>
            <Stack>
              {section.rows.map((row) => (
                <Typography
                  key={row.label}
                  sx={{ fontSize: "15px", lineHeight: "20px", color: row.error ? "#d32f2f" : "#071F35" }}
                >
                  {row.label}:{" "}
                  {row.error ? (
                    <Typography component="span" sx={{ fontSize: "15px", fontWeight: 700, color: "#d32f2f" }}>
                      Required
                    </Typography>
                  ) : (
                    <Typography component="span" sx={{ fontSize: "15px", fontWeight: 700, lineHeight: "20px" }}>
                      {row.value}
                    </Typography>
                  )}
                </Typography>
              ))}
            </Stack>
          </Stack>
        ))}
      </Stack>

      <NavigationButtons
        onBack={goBack}
        onNext={handleSubmit}
        nextLabel="Create Deal"
        nextDisabled={isCreateDisabled}
        {...(requiredCharsMissing && {
          nextTooltip: "Go back and complete the\nrequired fields to create a deal."
        })}
      />
    </>
  );
};
